import React from 'react';
import './ResponsiveGrid.css';

/**
 * ResponsiveGrid component with ArbiWin design system integration
 * Flexible grid system for responsive layouts
 *
 * @param {Object} props - ResponsiveGrid properties
 * @param {React.ReactNode} props.children - Grid items
 * @param {number|string} props.columns - Number of columns or responsive object
 * @param {string} props.gap - Gap between grid items: 'none', 'small', 'medium', 'large', 'xlarge'
 * @param {string} props.align - Vertical alignment: 'start', 'center', 'end', 'stretch'
 * @param {string} props.justify - Horizontal alignment: 'start', 'center', 'end', 'space-between', 'space-around'
 * @param {boolean} props.autoFit - Use auto-fit for responsive columns
 * @param {boolean} props.autoFill - Use auto-fill for responsive columns
 * @param {string} props.minItemWidth - Minimum width for auto-fit/auto-fill
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} ResponsiveGrid component
 */
const ResponsiveGrid = function ResponsiveGrid({
  children,
  columns = 'auto',
  gap = 'medium',
  align = 'stretch',
  justify = 'start',
  autoFit = false,
  autoFill = false,
  minItemWidth = '250px',
  className = '',
  testId
}) {

  const gridClasses = [
    'arbiwin-responsive-grid',
    'arbiwin-responsive-grid--gap-' + gap,
    'arbiwin-responsive-grid--align-' + align,
    'arbiwin-responsive-grid--justify-' + justify,
    autoFit ? 'arbiwin-responsive-grid--auto-fit' : '',
    autoFill ? 'arbiwin-responsive-grid--auto-fill' : '',
    className
  ].filter(Boolean).join(' ');

  const gridStyle = {};

  // Handle different column configurations
  if (autoFit || autoFill) {
    const fitType = autoFit ? 'auto-fit' : 'auto-fill';
    gridStyle.gridTemplateColumns = `repeat(${fitType}, minmax(${minItemWidth}, 1fr))`;
  } else if (typeof columns === 'object') {
    // Responsive columns handled via CSS classes
    gridStyle.gridTemplateColumns = null;
  } else if (typeof columns === 'number') {
    gridStyle.gridTemplateColumns = `repeat(${columns}, 1fr)`;
  } else if (typeof columns === 'string') {
    gridStyle.gridTemplateColumns = columns;
  }

  return (
    <div
      className={gridClasses}
      style={gridStyle}
      data-testid={testId || 'arbiwin-responsive-grid'}
    >
      {children}
    </div>
  );
};

/**
 * GridItem component for individual grid items with specific positioning
 *
 * @param {Object} props - GridItem properties
 * @param {React.ReactNode} props.children - Item content
 * @param {number} props.colSpan - Number of columns to span
 * @param {number} props.rowSpan - Number of rows to span
 * @param {number} props.colStart - Starting column position
 * @param {number} props.rowStart - Starting row position
 * @param {string} props.align - Self alignment: 'start', 'center', 'end', 'stretch'
 * @param {string} props.justify - Self justification: 'start', 'center', 'end', 'stretch'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} GridItem component
 */
const GridItem = function GridItem({
  children,
  colSpan,
  rowSpan,
  colStart,
  rowStart,
  align,
  justify,
  className = '',
  testId
}) {

  const itemClasses = [
    'arbiwin-grid-item',
    align ? 'arbiwin-grid-item--align-' + align : '',
    justify ? 'arbiwin-grid-item--justify-' + justify : '',
    className
  ].filter(Boolean).join(' ');

  const itemStyle = {};
  if (colSpan) itemStyle.gridColumnEnd = `span ${colSpan}`;
  if (rowSpan) itemStyle.gridRowEnd = `span ${rowSpan}`;
  if (colStart) itemStyle.gridColumnStart = colStart;
  if (rowStart) itemStyle.gridRowStart = rowStart;

  return (
    <div
      className={itemClasses}
      style={itemStyle}
      data-testid={testId || 'arbiwin-grid-item'}
    >
      {children}
    </div>
  );
};

/**
 * FooterGrid component - Pre-configured grid for footer layouts
 * Based on the header_and_footer.html mockup
 *
 * @param {Object} props - FooterGrid properties
 * @param {React.ReactNode} props.children - Footer sections
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FooterGrid component
 */
const FooterGrid = function FooterGrid({
  children,
  className = ''
}) {
  return (
    <ResponsiveGrid
      columns={3}
      gap="large"
      align="start"
      justify="start"
      className={'arbiwin-footer-grid ' + className}
    >
      {children}
    </ResponsiveGrid>
  );
};

/**
 * ContentGrid component - Pre-configured grid for main content layouts
 *
 * @param {Object} props - ContentGrid properties
 * @param {React.ReactNode} props.children - Content items
 * @param {string} props.variant - Grid variant: 'cards', 'list', 'masonry'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ContentGrid component
 */
const ContentGrid = function ContentGrid({
  children,
  variant = 'cards',
  className = ''
}) {

  const variantConfig = {
    cards: {
      autoFit: true,
      minItemWidth: '300px',
      gap: 'large'
    },
    list: {
      columns: 1,
      gap: 'medium'
    },
    masonry: {
      autoFit: true,
      minItemWidth: '250px',
      gap: 'medium'
    }
  };

  const config = variantConfig[variant] || variantConfig.cards;

  return (
    <ResponsiveGrid
      {...config}
      className={'arbiwin-content-grid arbiwin-content-grid--' + variant + ' ' + className}
    >
      {children}
    </ResponsiveGrid>
  );
};

ResponsiveGrid.displayName = 'ResponsiveGrid';
GridItem.displayName = 'GridItem';
FooterGrid.displayName = 'FooterGrid';
ContentGrid.displayName = 'ContentGrid';

export default ResponsiveGrid;
export { GridItem, FooterGrid, ContentGrid };
